import { ChangeEvent, memo, useState } from 'react'

import {
  ArrowRightIcon,
  AttentionCircleIcon,
  Button,
  FieldValidation,
  LightIcon,
  Selector,
  Tag,
  Text
} from '@cash-me/react-components'
import { getIn, useFormik } from 'formik'

import { useSubmitRegistration } from 'hooks/subdelegated/useSubmitRegistration'
import { CepValidationStatus, useValidateCep } from 'hooks/subdelegated/useValidateCep'
import { DataLayerProps } from 'models/data-layer'
import { applyMask } from 'utils/masks/applyMask'
import { pushDataLayer } from 'utils/push-data-layer'

import { assinaturaConjuntaOptions, firstStepFields, legalRepresentativeFields } from './data'
import { SubdelegatedRegistrationFormValues } from './form.model'
import { inputFactory } from './inputFactory'
import { legalRepresentativeInputFactory } from './legalRepresentativeInputFactory'
import { mapFormValuesToPayload } from './mappers'
import { PrivacyNotice } from './privacyNotice'
import * as S from './styles'
import { SubmissionSuccess } from './success'
import { validationSchema } from './validationSchema'

const getCepValidation = (
  cepStatus: CepValidationStatus,
  cepErrorMessage: string | null,
  formikError: string | undefined,
  formikTouched: boolean | undefined
): FieldValidation => {
  if (cepStatus === 'invalid') return { invalid: cepErrorMessage ?? '' }
  if (cepStatus === 'valid') return { valid: '' }

  return {
    ...(formikError && formikTouched && { invalid: formikError }),
    ...(!formikError && formikTouched && { valid: '' })
  }
}

type Props = {
  email?: string
  lpDocument?: string
  code: string
}

export const SubdelegatedRegistrationForm = memo<Props>(({ email, lpDocument, code }) => {
  const [step, setStep] = useState<1 | 2>(1)
  const { status: submitStatus, errorMessage: submitErrorMessage, submit } = useSubmitRegistration(code)

  const formik = useFormik<SubdelegatedRegistrationFormValues>({
    initialValues: {
      nomeCompleto: '',
      email: email ?? '',
      telefone: '',
      cep: '',
      cnpj: lpDocument ?? '',
      razaoSocial: '',
      nomeFantasia: '',
      cnae: '',
      assinaturaConjunta: 'NAO',
      representanteLegal1: { nome: '', cpf: '', dataNascimento: '', email: '' },
      representanteLegal2: { nome: '', cpf: '', dataNascimento: '', email: '' }
    },
    initialTouched: { assinaturaConjunta: true },
    validationSchema,
    onSubmit: async values => {
      const result = await submit(mapFormValuesToPayload(values))
      if (result === 'error') {
        pushDataLayer({
          category: 'Clique',
          action: 'Cadastro Substabelecido',
          label: 'Informações complementares - Erro ao enviar'
        })
      }
    }
  })

  const { validateCep, resetCepStatus, isCepLoading, cepStatus, cepErrorMessage } = useValidateCep()
  const cepValidation = getCepValidation(cepStatus, cepErrorMessage, formik.errors.cep, formik.touched.cep)

  const handleCepChange = (event: ChangeEvent<HTMLInputElement>) => {
    const maskedValue = applyMask(event.target.value, 'cep')
    formik.setFieldTouched('cep', true)
    formik.setFieldValue('cep', maskedValue)

    const digits = maskedValue.replace(/\D/g, '')
    if (digits.length < 8) {
      resetCepStatus()
      return
    }
    validateCep(digits)
  }

  const editableStepOneFields = firstStepFields.filter(field => !field.disabled)
  const stepOneHasFieldErrors = editableStepOneFields.reduce(
    (acc, field) => acc || !!formik.errors[field.id] || !formik.touched[field.id],
    false
  )
  const isCepPending = isCepLoading || cepStatus !== 'valid'
  const stepOneHasErrors = stepOneHasFieldErrors || isCepPending

  const isAssinaturaConjunta = formik.values.assinaturaConjunta === 'SIM'
  const stepTwoFieldPaths = [
    'assinaturaConjunta',
    ...legalRepresentativeFields.map(field => `representanteLegal1.${field.id}`),
    ...(isAssinaturaConjunta ? legalRepresentativeFields.map(field => `representanteLegal2.${field.id}`) : [])
  ]
  const stepTwoHasErrors = stepTwoFieldPaths.reduce(
    (acc, path) => acc || !!getIn(formik.errors, path) || !getIn(formik.touched, path),
    false
  )

  const handleNextStep = () => {
    const dataLayer: DataLayerProps = {
      category: 'Clique',
      action: 'Cadastro Substabelecido',
      label: 'Sobre você e a empresa - Próximo'
    }
    pushDataLayer(dataLayer)
    setStep(2)
  }

  const handleSubmitStepTwo = () => {
    const dataLayer: DataLayerProps = {
      category: 'Clique',
      action: 'Cadastro Substabelecido',
      label: 'Informações complementares - Enviar'
    }
    pushDataLayer(dataLayer)
    formik.handleSubmit()
  }

  if (submitStatus === 'success') {
    return (
      <S.FormWrapper>
        <SubmissionSuccess />
      </S.FormWrapper>
    )
  }

  return (
    <S.FormWrapper>
      <S.FormHeader>
        <Text size="heading4" weight="medium">
          {step === 1 ? 'Sobre você e a empresa' : 'Informações complementares'}
        </Text>
        <Tag color="primary">Etapa {step} de 2</Tag>
      </S.FormHeader>

      {step === 1 ? (
        <S.Form onSubmit={formik.handleSubmit}>
          <div className="inputs-wrapper">
            {firstStepFields.map(field =>
              inputFactory({
                inputProps: field,
                formikContext: formik,
                ...(field.id === 'cep' && {
                  onChange: handleCepChange,
                  loading: isCepLoading,
                  validation: cepValidation
                })
              })
            )}
          </div>

          <S.CnaeInfoBox>
            <LightIcon size={20} color="var(--colors-primary-icon-default)" />
            <S.CNAETextBox>
              <Text size="caption" weight="bold">
                O que é o CNAE?
              </Text>
              <Text weight="medium" size="caption" color="var(--colors-neutral-text-default)">
                CNAE é o código que define a atividade exercida por uma empresa ou profissional. Ele é necessário para
                abrir o CNPJ e influência em tributos e obrigações legais.
              </Text>
            </S.CNAETextBox>
          </S.CnaeInfoBox>

          <PrivacyNotice />

          <Button type="button" size="lg" onClick={handleNextStep} disabled={stepOneHasErrors}>
            Próximo
          </Button>
        </S.Form>
      ) : (
        <S.Form onSubmit={formik.handleSubmit}>
          <div className="inputs-wrapper">
            <S.AssinaturaConjuntaFieldset>
              <legend>
                <Text as="span" weight="medium">
                  A assinatura é conjunta?
                </Text>
              </legend>
              <S.SelectorGroup>
                {assinaturaConjuntaOptions.map(option => (
                  <Selector
                    key={option.value}
                    id={`assinaturaConjunta-${option.value}`}
                    name="assinaturaConjunta"
                    value={option.value}
                    title={option.title}
                    checked={formik.values.assinaturaConjunta === option.value}
                    onChange={event => {
                      formik.setFieldTouched('assinaturaConjunta', true)
                      formik.handleChange(event)
                    }}
                  />
                ))}
              </S.SelectorGroup>
            </S.AssinaturaConjuntaFieldset>

            <S.RepresentativeSection>
              {isAssinaturaConjunta ? <Text weight="medium">1° Representante Legal</Text> : null}
              {legalRepresentativeFields.map(field =>
                legalRepresentativeInputFactory({
                  inputProps: field,
                  namePrefix: 'representanteLegal1',
                  representativeLabel: '1° representante legal',
                  formikContext: formik
                })
              )}
            </S.RepresentativeSection>

            {isAssinaturaConjunta ? (
              <S.RepresentativeSection>
                <Text weight="medium">2° Representante Legal</Text>
                {legalRepresentativeFields.map(field =>
                  legalRepresentativeInputFactory({
                    inputProps: field,
                    namePrefix: 'representanteLegal2',
                    representativeLabel: '2° representante legal',
                    formikContext: formik
                  })
                )}
              </S.RepresentativeSection>
            ) : null}
          </div>

          {submitStatus === 'error' && (
            <S.SubmitErrorBox aria-live="polite">
              <AttentionCircleIcon size={20} color="var(--colors-danger-icon-default)" />
              <Text size="caption" weight="medium" color="var(--colors-neutral-text-default)">
                {submitErrorMessage}
              </Text>
            </S.SubmitErrorBox>
          )}

          <PrivacyNotice />

          <Button
            type="button"
            size="lg"
            onClick={handleSubmitStepTwo}
            disabled={stepTwoHasErrors || submitStatus === 'submitting'}
            loading={submitStatus === 'submitting'}
          >
            Enviar <ArrowRightIcon />
          </Button>
        </S.Form>
      )}
    </S.FormWrapper>
  )
})

SubdelegatedRegistrationForm.displayName = 'SubdelegatedRegistrationForm'
